import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Modal,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import {useAlert} from "../context/AlertContext";
import AppTableHeaderCell from "./AppTableHeaderCell";
import AppModalBox from "./AppModalBox";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

import {
    deleteCompanyUser,
    getCompanyUserById, getCompanyUserRoles,
    getCompanyUsers,
    updateCompanyUser,
} from "../api/api";
import {CompanyUser, UserProfileResponse, UserRole} from "../types";

type Props = {
    companyId: number;
    refreshTrigger: boolean;
    refreshUsers: () => void;
    onAdd: () => void;
};

const getInitials = (fullName?: string) => {
    if (!fullName || !fullName.trim()) return "?";

    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const CompanyUsersList = ({
                              companyId,
                              refreshTrigger,
                              refreshUsers,
                              onAdd,
                          }: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();

    const [users, setUsers] = useState<CompanyUser[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchEmail, setSearchEmail] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const pageSize = 10;

    const [loading, setLoading] = useState(false);

    const [userToDelete, setUserToDelete] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
    const [editModalLoading, setEditModalLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    const [roles, setRoles] = useState<UserRole[]>([]);
    const [rolesLoading, setRolesLoading] = useState(false);

    const [editFullName, setEditFullName] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [editPassword, setEditPassword] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);

    const fetchUsers = async () => {
        try {
            setLoading(true);

            const {users, totalPages} = await getCompanyUsers(
                companyId,
                page - 1,
                pageSize,
                searchEmail
            );

            setUsers(users);
            setTotalPages(totalPages);
        } catch (error: any) {
            showAlert(error?.message ?? t("companyuserslist.errors.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, page, refreshTrigger, searchEmail]);

    useEffect(() => {
        setPage(1);
    }, [searchEmail]);

    const openEdit = async (user: CompanyUser) => {
        try {
            setEditingUser(user);
            setEditModalLoading(true);

            let allRoles = roles;

            if (!allRoles.length) {
                setRolesLoading(true);
                allRoles = await getCompanyUserRoles();
                setRoles(allRoles);
            }

            const details: UserProfileResponse = await getCompanyUserById(companyId, user.id);

            setEditFullName(details.fullName ?? "");
            setEditEmail(details.email ?? "");
            setEditPassword("");

            const byId = new Set(
                ((details.roles ?? []) as any[])
                    .map((r) => (r && typeof r === "object" ? (r as any).id : null))
                    .filter((id): id is number => typeof id === "number")
            );

            const byName = new Set((details.roles ?? []).map((r) => r.name));

            const preselected = allRoles.filter(
                (role) => byId.has(role.id) || byName.has(role.name)
            );

            setSelectedRoles(preselected);

            if (preselected.length === 0) {
                showAlert(t("companyuserslist.alerts.noRolesSelected"), "warning");
            }
        } catch (error: any) {
            showAlert(error?.message || t("companyuserslist.errors.loadDetailsFailed"), "error");
            setEditingUser(null);
        } finally {
            setEditModalLoading(false);
            setRolesLoading(false);
        }
    };

    const closeEditModal = () => {
        setEditingUser(null);
        setEditModalLoading(false);
        setEditSaving(false);
        setEditPassword("");
        setSelectedRoles([]);
    };

    const handleEditUser = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!editingUser) return;

        if (!editFullName.trim()) {
            showAlert(t("companyuserslist.errors.fullNameRequired"), "error");
            return;
        }

        if (!editEmail.trim()) {
            showAlert(t("companyuserslist.errors.emailRequired"), "error");
            return;
        }

        if (selectedRoles.length === 0) {
            showAlert(t("companyuserslist.errors.atLeastOneRoleRequired"), "error");
            return;
        }

        try {
            setEditSaving(true);

            await updateCompanyUser(companyId, {
                id: editingUser.id,
                fullName: editFullName.trim(),
                email: editEmail.trim(),
                ...(editPassword.trim() ? {password: editPassword.trim()} : {}),
                roles: selectedRoles.map((role) => ({id: role.id})),
            });

            showAlert(t("companyuserslist.alerts.updated"), "success");
            closeEditModal();
            refreshUsers?.();
        } catch (error: any) {
            showAlert(error?.message || t("companyuserslist.errors.updateFailed"), "error");
        } finally {
            setEditSaving(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setDeleteLoading(true);

            await deleteCompanyUser(companyId, userToDelete);

            showAlert(t("companyuserslist.alerts.deleted"), "success");
            setUserToDelete(null);
            refreshUsers?.();
        } catch (error: any) {
            showAlert(error?.message || t("companyuserslist.errors.deleteFailed"), "error");
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: {xs: 2, md: 3},
                            py: 2,
                            flexWrap: "wrap",
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <TextField
                            type="search"
                            size="small"
                            placeholder={t("companyuserslist.searchByEmail")}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setSearchEmail(searchInput.trim());
                                }
                            }}
                            sx={{width: {xs: "100%", sm: 320}}}
                        />

                        <IconButton
                            onClick={() => setSearchEmail(searchInput.trim())}
                            disabled={loading}
                            aria-label={t("common.search")}
                            sx={(theme) => ({
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                "&:hover": {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            })}
                        >
                            <SearchIcon fontSize="small"/>
                        </IconButton>

                        <Box sx={{flexGrow: 1}}/>

                        <Typography variant="body2" color="text.secondary">
                            {t("common.pageOf", {page, totalPages})}
                        </Typography>

                        <IconButton
                            onClick={onAdd}
                            aria-label={t("companyuserslist.addCompanyUser")}
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                "&:hover": {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            })}
                        >
                            <AddIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    {users.length === 0 ? (
                        <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                {t("companyuserslist.noCompanyUsersFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("companyuserslist.noCompanyUsersDescription")}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{width: "100%", overflowX: "auto"}}>
                            <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                <thead>
                                <tr style={{background: "#fafafa"}}>
                                    <AppTableHeaderCell>
                                        {t("companyuserslist.user")}
                                    </AppTableHeaderCell>

                                    <AppTableHeaderCell>
                                        {t("companyuserslist.email")}
                                    </AppTableHeaderCell>

                                    <AppTableHeaderCell align="right">
                                        {t("common.actions")}
                                    </AppTableHeaderCell>
                                </tr>
                                </thead>

                                <tbody>
                                {users.map((user) => (
                                    <tr
                                        key={user.id}
                                        style={{
                                            transition: "background-color 0.2s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#fafafa";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <td style={{padding: "18px 16px"}}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        bgcolor: "grey.300",
                                                        color: "text.primary",
                                                    }}
                                                >
                                                    {getInitials(user.fullName)}
                                                </Avatar>

                                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                                    {user.fullName || "—"}
                                                </Typography>
                                            </Box>
                                        </td>

                                        <td style={{padding: "18px 16px"}}>
                                            <Typography variant="body2">
                                                {user.email}
                                            </Typography>
                                        </td>

                                        <td style={{padding: "18px 16px", textAlign: "right"}}>
                                            <Box sx={{display: "inline-flex", gap: 1}}>
                                                <Tooltip title={t("common.edit")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openEdit(user)}
                                                        aria-label={t("common.edit")}
                                                        sx={(theme) => ({
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: "50%",
                                                            backgroundColor: theme.palette.action.hover,
                                                            color: theme.palette.text.primary,
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.action.selected,
                                                            },
                                                        })}
                                                    >
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title={t("common.delete")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setUserToDelete(user.id)}
                                                        aria-label={t("common.delete")}
                                                        sx={(theme) => ({
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: "50%",
                                                            backgroundColor: theme.palette.action.hover,
                                                            color: theme.palette.text.primary,
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.action.selected,
                                                            },
                                                        })}
                                                    >
                                                        <DeleteIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Paper>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>

            <DeleteConfirmationModal
                open={userToDelete !== null}
                onClose={() => (deleteLoading ? null : setUserToDelete(null))}
                onConfirm={handleDeleteUser}
            />

            <Modal open={editingUser !== null} onClose={closeEditModal}>
                <AppModalBox
                    sx={{
                        p: 4,
                        margin: "auto",
                        width: 520,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {t("companyuserslist.editCompanyUser")}
                    </Typography>

                    {editModalLoading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <form onSubmit={handleEditUser}>
                            <TextField
                                label={t("companyuserslist.fullName")}
                                fullWidth
                                required
                                value={editFullName}
                                onChange={(e) => setEditFullName(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("companyuserslist.email")}
                                type="email"
                                fullWidth
                                required
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("companyuserslist.newPasswordOptional")}
                                type="password"
                                fullWidth
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                disabled={editSaving}
                                helperText={t("companyuserslist.keepCurrentPassword")}
                                sx={{mb: 2}}
                            />

                            <Autocomplete
                                multiple
                                options={roles}
                                loading={rolesLoading}
                                value={selectedRoles}
                                onChange={(_, value) => setSelectedRoles(value)}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("companyuserslist.roles")}
                                        placeholder={
                                            rolesLoading
                                                ? t("common.loading")
                                                : t("companyuserslist.selectRoles")
                                        }
                                        disabled={editSaving || rolesLoading}
                                        sx={{mb: 2}}
                                    />
                                )}
                            />

                            <Box sx={{display: "flex", gap: 1, justifyContent: "flex-end"}}>
                                <Button onClick={closeEditModal} disabled={editSaving}>
                                    {t("common.cancel")}
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={editSaving || rolesLoading || selectedRoles.length === 0}
                                >
                                    {editSaving ? t("companyuserslist.saving") : t("companyuserslist.saveChanges")}
                                </Button>
                            </Box>

                            <Typography
                                variant="caption"
                                sx={{display: "block", mt: 2, color: "text.secondary"}}
                            >
                                {t("companyuserslist.passwordUpdateNote")}
                            </Typography>
                        </form>
                    )}
                </AppModalBox>
            </Modal>
        </>
    );
};

export default CompanyUsersList;